import * as React from "react";
import { Eyeglasses, PencilSimple, PlusSquare, ChartLine, FolderOpen, Blueprint, MagnifyingGlass, Stamp, GlobeSimple, Slideshow, Image, FileMagnifyingGlass, FileXls, Table, PaintBrush, TreeStructure, ChatCircleDots, Sparkle, ListMagnifyingGlass, Wrench, } from "@phosphor-icons/react";
import { AlphaSenseIcon, SearchIcon } from "../../icons";
const createConfig = (activeName, completedName, icon, singularActiveName, singularCompletedName) => ({
    activeName,
    completedName,
    singularActiveName,
    singularCompletedName,
    icon,
});
const I = 16;
const icon = (Icon, className = "shrink-0") => React.createElement(Icon, { size: I, className });
const DEFAULT_ICON = icon(Wrench);
const inferSlideCount = (params) => {
    if (!params)
        return undefined;
    if (Array.isArray(params.operations)) {
        return params.operations.length;
    }
    const candidates = [
        params.slideIndices,
        params.slide_indexes,
        params.targetSlides,
        params.slides,
        params.slideIds,
        params.slide_ids,
    ];
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate.length;
        }
    }
    if (params.slideIndex !== undefined || params.slide_index !== undefined)
        return 1;
    if (params.slideId !== undefined || params.slide_id !== undefined)
        return 1;
    return undefined;
};
export const TOOL_REGISTRY = {
    readSlide: createConfig("Reading slides...", "Read slides", icon(Eyeglasses), "Reading slide...", "Read slide"),
    read_slide: createConfig("Reading slides...", "Read slides", icon(Eyeglasses), "Reading slide...", "Read slide"),
    writeSlide: createConfig("Editing slides", "Edited slides", icon(PencilSimple), "Editing slide", "Edited slide"),
    addSlides: createConfig("Adding slides", "Added slides", icon(PlusSquare), "Adding slide", "Added slide"),
    reorderOrDeleteSlides: createConfig("Preparing slide operations", "Applied slide operations", icon(Slideshow)),
    reorder_or_delete_slides: createConfig("Preparing slide operations", "Applied slide operations", icon(Slideshow)),
    getChartPointPositions: createConfig("Checking chart point positions", "Checked chart point positions", icon(ChartLine)),
    getRelevantExcelFiles: createConfig("Finding relevant excel files", "Found relevant excel files", icon(FolderOpen)),
    generateBlueprint: createConfig("Creating a slide blueprint", "Created a slide blueprint", icon(Blueprint)),
    searchBrandfetch: createConfig("Searching for logos & brand kits", "Searched for logos & brand kits", icon(MagnifyingGlass)),
    retrieve_brandfetch: createConfig("Pulling down logos", "Pulled down logos", icon(Stamp)),
    retrieveBrandfetch: createConfig("Pulling down logos", "Pulled down logos", icon(Stamp)),
    searchWikimedia: createConfig("Searching public domain images", "Searched public domain images", icon(Image)),
    retrieveWikimedia: createConfig("Pulling down public domain images", "Pulled down public domain images", icon(GlobeSimple)),
    searchPresentationImages: createConfig("Searching existing presentation images", "Searched existing presentation images", icon(Image)),
    searchPresentation: createConfig("Searching presentation content", "Searched presentation content", React.createElement(SearchIcon, { className: "shrink-0" })),
    currentPresentationSearch: createConfig("Searching current presentation", "Searched current presentation", React.createElement(SearchIcon, { className: "shrink-0" })),
    retrievePresentationImage: createConfig("Grabbing images from the presentation", "Grabbed images from the presentation", icon(Slideshow)),
    searchFiles: createConfig("Searching external files", "Searched external files", icon(FileMagnifyingGlass)),
    readExcel: createConfig("Reading your excel file", "Read your excel file", icon(FileXls)),
    searchAlphaSense: createConfig("Searching AlphaSense GenSearch", "Searched AlphaSense GenSearch", icon(ListMagnifyingGlass)),
    alphasense_search: createConfig("Searching AlphaSense GenSearch", "Searched AlphaSense GenSearch", icon(ListMagnifyingGlass)),
    alphaSenseSearch: createConfig("Searching AlphaSense GenSearch", "Searched AlphaSense GenSearch", icon(ListMagnifyingGlass)),
    editStructure: createConfig("Editing workbook structure", "Edited workbook structure", icon(TreeStructure)),
    editContent: createConfig("Editing cell content", "Edited cell content", icon(Table)),
    editFormat: createConfig("Editing cell format", "Edited cell format", icon(PaintBrush)),
    editChart: createConfig("Editing chart format", "Edited chart format", icon(ChartLine)),
    alphaSenseSearchAgent: createConfig("Asking AlphaSense", "Asked AlphaSense", React.createElement(AlphaSenseIcon, { size: I })),
    presentationLibrarySearchAgent: createConfig("Searching presentation library", "Searched presentation library", icon(MagnifyingGlass)),
    PresentationLibrarySearchAgent: createConfig("Searching presentation library", "Searched presentation library", icon(MagnifyingGlass)),
    // Legacy display aliases for historical chat rendering.
    inPresentationSearchAgent: createConfig("Searching presentation library", "Searched presentation library", icon(MagnifyingGlass)),
    InPresentationSearchAgent: createConfig("Searching presentation library", "Searched presentation library", icon(MagnifyingGlass)),
    imageSearchAgent: createConfig("Searching images", "Searched images", icon(Image)),
    ImageSearchAgent: createConfig("Searching images", "Searched images", icon(Image)),
    externalFileSearchAgent: createConfig("Searching external files", "Searched external files", icon(FileMagnifyingGlass)),
    ExternalFileSearchAgent: createConfig("Searching external files", "Searched external files", icon(FileMagnifyingGlass)),
    linkedExcelEditorAgent: createConfig("Editing linked workbook", "Edited linked workbook", icon(FileXls)),
    editContentAgent: createConfig("Editing cell content", "Edited cell content", icon(Table)),
    editFormatAgent: createConfig("Editing cell format", "Edited cell format", icon(PaintBrush)),
    editChartAgent: createConfig("Editing chart format", "Edited chart format", icon(ChartLine)),
    userInterview: createConfig("Gathering details", "Gathered details", icon(ChatCircleDots)),
    promptGet: createConfig("Retrieving prompt", "Retrieved prompt", icon(Eyeglasses)),
    prompt_get: createConfig("Retrieving prompt", "Retrieved prompt", icon(Eyeglasses)),
    promptSearch: createConfig("Searching prompts", "Searched prompts", icon(MagnifyingGlass)),
    prompt_search: createConfig("Searching prompts", "Searched prompts", icon(MagnifyingGlass)),
    submitResults: createConfig("Finalizing response", "Finalized response", icon(Sparkle)),
    submit_results: createConfig("Finalizing response", "Finalized response", icon(Sparkle)),
    create_plan: createConfig("Creating presentation outline", "Created presentation outline", icon(Sparkle)),
    edit_plan: createConfig("Editing plan", "Edited plan", icon(PencilSimple)),
    search_alphasense: createConfig("Searching AlphaSense", "Searched AlphaSense", icon(ListMagnifyingGlass)),
    alphasense_research: createConfig("Researching with AlphaSense", "Researched with AlphaSense", icon(ListMagnifyingGlass)),
    get_user_preferences: createConfig("Gathering preferences", "Gathered preferences", icon(ChatCircleDots)),
};
const formatDisplayName = (str) => {
    const match = str.match(/^(.+)\[(\d+)\]$/);
    if (match) {
        return `${formatDisplayName(match[1])}[${match[2]}]`;
    }
    if (str.includes("_")) {
        return str
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    }
    return str
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
};
export const getToolDisplay = (toolName, isComplete, params) => {
    const entry = TOOL_REGISTRY[toolName];
    if (entry) {
        const slideCount = inferSlideCount(params);
        const useSingular = slideCount === 1;
        return {
            name: isComplete
                ? useSingular
                    ? (entry.singularCompletedName ?? entry.completedName)
                    : entry.completedName
                : useSingular
                    ? (entry.singularActiveName ?? entry.activeName)
                    : entry.activeName,
            icon: entry.icon,
        };
    }
    return {
        name: formatDisplayName(toolName),
        icon: DEFAULT_ICON,
    };
};
